import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/components/UI";
import { CaretDown, User } from "@phosphor-icons/react";
import { GroupedTable, toggleWithScrollPreserve } from "@/components/VetTable";

const TR_MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

const BIRTH_TYPES = [
  { key: "normal", label: "Normal", bg: "#E4EEE6", fg: "#2D4A36", dot: "#2D6A4F" },
  { key: "güç", label: "Güç", bg: "#fbeee9", fg: "#8a3a2b", dot: "#C45B45" },
  { key: "sezaryen", label: "Sezaryen", bg: "#F5E9D3", fg: "#8B6914", dot: "#DDA74F" },
];

// Doğum/Atık kayıtlarını Yıl → Ay kartları halinde gruplar (tohumlama modülüyle aynı desen).
export function BirthMonthlyCards({ items, columns, dayMeta }) {
  const [openKey, setOpenKey] = useState(null);
  const didInit = useRef(false);

  const years = useMemo(() => {
    const byMonth = {};
    for (const x of items) {
      const d = (x.date || "").slice(0, 7);
      if (!/^\d{4}-\d{2}$/.test(d)) continue;
      if (!byMonth[d]) byMonth[d] = {
        key: d, records: [], births: 0, aborts: 0, inek: 0, duve: 0,
        female: 0, male: 0, stillborn: 0, type: { normal: 0, "güç": 0, sezaryen: 0 }, vets: {},
      };
      const m = byMonth[d];
      m.records.push(x);
      const vet = (x.vet_name || "").trim();
      if (vet) m.vets[vet] = (m.vets[vet] || 0) + 1;
      if (x.kind === "abort") {
        m.aborts += Number(x.abort_count || 1);
      } else {
        m.births += 1;
        if (x.animal_type === "düve") m.duve += 1; else m.inek += 1;
        m.female += Number(x.female_count || 0);
        m.male += Number(x.male_count || 0);
        m.stillborn += Number(x.stillborn_count || 0);
        const bt = x.birth_type || "normal";
        if (m.type[bt] !== undefined) m.type[bt] += 1;
      }
    }
    for (const k in byMonth) {
      const m = byMonth[k];
      // O ay kayıt giren tüm veterinerler, kayıt sayısına göre azalan sırada
      m.topVets = Object.entries(m.vets)
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "tr"))
        .map(([name, count]) => ({ name, count }));
      m.records.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    }
    const byYear = {};
    Object.values(byMonth).forEach(m => {
      const y = m.key.slice(0, 4);
      (byYear[y] = byYear[y] || []).push(m);
    });
    return Object.entries(byYear)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([year, months]) => ({
        year,
        months: months.sort((a, b) => b.key.localeCompare(a.key)),
        births: months.reduce((s, m) => s + m.births, 0),
        aborts: months.reduce((s, m) => s + m.aborts, 0),
      }));
  }, [items]);

  // İlk yüklemede en güncel ayı otomatik aç
  useEffect(() => {
    if (!didInit.current && years.length) {
      setOpenKey(years[0].months[0].key);
      didInit.current = true;
    }
  }, [years]);

  if (!items.length) return <Card className="lg:col-span-2 text-center text-[#8a938c] py-10" testId="birth-monthly-empty">Henüz doğum kaydı yok.</Card>;

  return (
    <div className="lg:col-span-2 space-y-5" data-testid="birth-monthly-cards">
      {years.map(({ year, months, births, aborts }) => (
        <div key={year}>
          <div className="flex items-center gap-3 mb-2.5">
            <h3 className="heading text-lg text-[#1f3326]" data-testid={`birth-year-${year}`}>{year}</h3>
            <span className="text-xs text-[#8a938c]">{births} doğum{aborts > 0 ? ` · ${aborts} atık` : ""} · {months.length} ay</span>
            <div className="flex-1 h-px bg-[#E5E2DC]" />
          </div>
          <div className="space-y-2.5">
            {months.map(m => {
              const label = `${TR_MONTHS[Number(m.key.slice(5, 7)) - 1]} ${year}`;
              const open = openKey === m.key;
              return (
                <Card key={m.key} className="!p-0 overflow-hidden" testId={`birth-month-${m.key}`}>
                  <button type="button" onClick={(e) => toggleWithScrollPreserve(e, m.key, setOpenKey)} data-testid={`birth-month-toggle-${m.key}`}
                    className="w-full text-left px-4 py-3.5 hover:bg-[#F7F6F3] transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-[#1f3326] text-base">{label}</span>
                          <span className="tabnum text-sm font-bold text-white bg-[#2D4A36] px-2.5 py-0.5 rounded-full" data-testid={`birth-month-total-${m.key}`}>{m.births} doğum</span>
                          {m.aborts > 0 && (
                            <span className="tabnum text-sm font-bold text-white bg-[#C45B45] px-2.5 py-0.5 rounded-full" data-testid={`birth-month-aborts-${m.key}`}>{m.aborts} atık</span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-2 text-xs">
                          <span className="px-2 py-1 rounded-md bg-[#EEF3EF] text-[#2D4A36] font-medium">İnek: <b className="tabnum">{m.inek}</b></span>
                          <span className="px-2 py-1 rounded-md bg-[#F2EFE8] text-[#5e675e] font-medium">Düve: <b className="tabnum">{m.duve}</b></span>
                          <span className="w-px h-4 bg-[#E5E2DC] mx-0.5" />
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#E4EEE6] text-[#2D4A36] font-medium"><span className="w-2 h-2 rounded-sm" style={{ background: "#2D6A4F" }} />Canlı Dişi: <b className="tabnum">{m.female}</b></span>
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#FBF1DE] text-[#8B6914] font-medium"><span className="w-2 h-2 rounded-sm" style={{ background: "#DDA74F" }} />Canlı Erkek: <b className="tabnum">{m.male}</b></span>
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#FBECEA] text-[#8B2E1D] font-medium"><span className="w-2 h-2 rounded-sm" style={{ background: "#C45B45" }} />Ölü Doğan: <b className="tabnum">{m.stillborn}</b></span>
                        </div>
                        {m.births > 0 && (
                          <div className="flex flex-wrap items-center gap-1.5 mt-2 text-xs" data-testid={`birth-month-types-${m.key}`}>
                            {BIRTH_TYPES.map(bt => (
                              <span key={bt.key} className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-medium" style={{ background: bt.bg, color: bt.fg }}>
                                <span className="w-2 h-2 rounded-sm" style={{ background: bt.dot }} />{bt.label}: <b className="tabnum">{m.type[bt.key]}</b>
                              </span>
                            ))}
                          </div>
                        )}
                        {m.topVets.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1.5 mt-2 text-xs" data-testid={`birth-month-vets-${m.key}`}>
                            <User size={13} weight="fill" className="text-[#2D4A36]" />
                            {m.topVets.map(v => (
                              <span key={v.name} className="px-2 py-0.5 rounded-full bg-[#EEF3EF] border border-[#DCE6DE] font-medium text-[#2D4A36]">
                                {v.name} <b className="tabnum">({v.count})</b>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <CaretDown size={18} weight="bold" className={`text-[#8a938c] mt-1 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                    </div>
                  </button>
                  {open && (
                    <div className="border-t border-[#ECE8E0]" data-testid={`birth-month-detail-${m.key}`}>
                      <GroupedTable testId={`birth-m-${m.key}`} items={m.records} pageSize={1000}
                        emptyText="Kayıt yok" dayMeta={dayMeta} columns={columns}
                        className="!border-0 !rounded-none" />
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
